/**
 * Claimer-conditional `userShareD6` updater for `useGetMarketBackerEarnings`
 * receiving a per-market `morfi.market.${m}.backer_fee_claim.v0` event.
 *
 * The subject is PUBLIC (any claim on this market broadcasts to all
 * viewers). The declarative `set:` recipes update `vaultD6` and `supplyD6`
 * from authoritative chain snapshots — those apply to every viewer's
 * cache regardless of identity. But the claimer's own `userShareD6` MUST
 * decrement by the burn amount, otherwise `recomputeBackerEarnings` will
 * compute `(stale userShare × new vault) / new supply ≈ pre-claim earned`
 * — the claimer sees their earned amount stuck at the pre-claim value
 * until staleTime refetch.
 *
 * Returns `prev` unchanged when:
 *   - The event's `claimer` is not the connected user
 *   - `userWallet` is null (anonymous viewer)
 *   - The burn amount is missing or unparseable
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { type BackerEarningsRowLike, isViewerWallet } from './apply-backing-to-backer-earnings.js';

function safeBigInt(value: unknown): bigint | null {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string') {
    if (!/^-?\d+$/.test(value.trim())) return null;
    try {
      return BigInt(value.trim());
    } catch {
      return null;
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return BigInt(Math.trunc(value));
  }
  return null;
}

export function applyClaimToBackerEarnings<T extends BackerEarningsRowLike>(
  prev: T,
  raw: Record<string, unknown>,
  args: Record<string, unknown> = {},
): T {
  if (typeof raw.claimer !== 'string' || !isViewerWallet(prev, args, raw.claimer)) return prev;
  const burn = safeBigInt(raw.burnAmountD6);
  if (burn === null || burn === 0n) return prev;
  const current = safeBigInt(prev.userShareD6 ?? '0') ?? 0n;
  const next = current - burn;
  const clamped = next < 0n ? 0n : next;
  // The claim's USDC payout accumulates into the viewer's lifetime
  // `claimedTotalD6` so claimed-inclusive displays don't drop to zero the
  // moment the share burn zeroes `earnedD6` (MOR-1835). Only applied when the
  // event carries the payout, so payload-less callers keep prior behaviour.
  const payout = safeBigInt(raw.quotePayoutD6);
  const shareChanged = clamped.toString() !== (prev.userShareD6 ?? '0');
  if (payout === null || payout <= 0n) {
    if (!shareChanged) return prev;
    return { ...prev, userShareD6: clamped.toString() };
  }
  const claimedCurrent = safeBigInt(prev.claimedTotalD6 ?? '0') ?? 0n;
  return {
    ...prev,
    userShareD6: clamped.toString(),
    claimedTotalD6: (claimedCurrent + payout).toString(),
  };
}
